import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { LOGO_URL } from "@/i18n/translations";

// Seasonal site themes — Dev → THEMES toggles one of: christmas | halloween | apokries | easter | summer.
// Palette switches via html[data-theme] CSS vars (index.css); this component renders the animated decorations.

const reducedMotion = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ---------- Particle canvases (snow / confetti) ---------- */
const ParticleCanvas = ({ kind }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (reducedMotion()) return;
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let W, H;
    const resize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);

    const CONFETTI_COLORS = ["#C42B9C", "#14A496", "#F2C230", "#3D7BE0", "#E8563F", "#8C4BC4"];
    const N = kind === "snow" ? 110 : 90;
    const parts = Array.from({ length: N }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: kind === "snow" ? 1.2 + Math.random() * 2.6 : 4 + Math.random() * 5,
      vy: kind === "snow" ? 0.4 + Math.random() * 0.9 : 1.1 + Math.random() * 1.6,
      vx: (Math.random() - 0.5) * (kind === "snow" ? 0.5 : 0.9),
      a: Math.random() * Math.PI * 2,
      va: (Math.random() - 0.5) * 0.08,
      c: CONFETTI_COLORS[(Math.random() * CONFETTI_COLORS.length) | 0],
      circle: Math.random() > 0.6,
      o: 0.55 + Math.random() * 0.45,
    }));

    const tick = () => {
      ctx.clearRect(0, 0, W, H);
      for (const p of parts) {
        p.y += p.vy;
        p.x += p.vx + (kind === "snow" ? Math.sin(p.y / 60) * 0.3 : Math.sin(p.y / 45) * 0.6);
        p.a += p.va;
        if (p.y > H + 12) { p.y = -12; p.x = Math.random() * W; }
        if (p.x > W + 12) p.x = -12;
        if (p.x < -12) p.x = W + 12;
        ctx.globalAlpha = p.o;
        if (kind === "snow") {
          ctx.fillStyle = "#FFFFFF";
          ctx.shadowColor = "rgba(120, 150, 190, 0.85)";
          ctx.shadowBlur = 4;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;
        } else {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.a);
          ctx.fillStyle = p.c;
          if (p.circle) {
            ctx.beginPath();
            ctx.arc(0, 0, p.r / 1.6, 0, Math.PI * 2);
            ctx.fill();
          } else {
            ctx.fillRect(-p.r / 2, -p.r / 4, p.r, p.r / 2);
          }
          ctx.restore();
        }
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, [kind]);
  if (reducedMotion()) return null;
  return <canvas ref={ref} aria-hidden="true" />;
};

/* ---------- Christmas ---------- */
const BULB_COLORS = ["#E8563F", "#F2C230", "#1F6B3A", "#3D7BE0"];

// Festoon garland: bulbs hang exactly ON the wire — both share the same scallop math
const Garland = () => {
  const SCALLOPS = 3;
  const wireY = (fx) => 4 + 10 * (0.5 - 0.5 * Math.cos(fx * Math.PI * 2 * SCALLOPS)); // 0..1 → px in 0..14 box
  const N_BULBS = 24;
  const samples = Array.from({ length: 61 }, (_, i) => i / 60);
  const path = samples.map((fx, i) => `${i === 0 ? "M" : "L"} ${(fx * 100).toFixed(2)} ${wireY(fx).toFixed(2)}`).join(" ");
  return (
    <div className="absolute left-0 right-0 top-16 sm:top-[4.5rem] h-12" aria-hidden="true">
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 14" preserveAspectRatio="none">
        <path d={path} fill="none" stroke="#1F6B3A" strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
      </svg>
      {Array.from({ length: N_BULBS }).map((_, i) => {
        const fx = (i + 0.5) / N_BULBS;
        const y = wireY(fx);
        const c = BULB_COLORS[i % BULB_COLORS.length];
        return (
          <span
            key={i}
            className="fx-bulb absolute rounded-full"
            style={{
              left: `${fx * 100}%`,
              top: `${(y / 14) * 100}%`,
              width: 9,
              height: 9,
              marginLeft: -4.5,
              marginTop: 2,
              background: c,
              boxShadow: `0 0 8px ${c}, 0 0 3px #fff inset`,
              animationDelay: `${(i * 0.19).toFixed(2)}s`,
            }}
          />
        );
      })}
    </div>
  );
};

// Santa's sleigh (user-provided illustration) glides across the sky every ~42s
const SleighFlyby = () => (
  <img src="/santa-sleigh.png" alt="" className="fx-sleigh-img" aria-hidden="true" />
);

const Snowflake = ({ className }) => (
  <svg className={`fx-snowflake absolute text-white/70 ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M12 2v20M4 6l16 12M20 6L4 18M12 2l-2 3m2-3l2 3M12 22l-2-3m2 3l2-3M4 6l3.5.5M4 6L4.5 9.5M20 18l-3.5-.5m3.5.5l-.5-3.5M20 6l-3.5.5M20 6l-.5 3.5M4 18l3.5-.5M4 18l.5-3.5" />
  </svg>
);

// Entry splash (christmas, dev-toggleable): logo + the user's wreath with the days inside + a loading bar, gone after 4s.
// Rendered in a body portal so it covers EVERYTHING (navbar included).
const ChristmasSplash = () => {
  const { t } = useLang();
  const th = t.themes;
  const [show, setShow] = useState(true);
  useEffect(() => {
    const tmr = setTimeout(() => setShow(false), 4000);
    return () => clearTimeout(tmr);
  }, []);

  const [days, label] = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const xmas = new Date(year, 11, 25, 0, 0, 0);
    const newYear = new Date(year + 1, 0, 1, 0, 0, 0);
    if (now < xmas) return [Math.max(0, Math.ceil((xmas - now) / 86400000)), th.splash_xmas];
    if (now < newYear) return [Math.max(0, Math.ceil((newYear - now) / 86400000)), th.splash_newyear];
    return [Math.max(0, Math.ceil((new Date(year + 1, 11, 25) - now) / 86400000)), th.splash_xmas];
  }, [th]);

  return createPortal(
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[999] grid place-items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.35 } }}
          exit={{ opacity: 0, scale: 1.05, transition: { duration: 0.55, ease: "easeInOut" } }}
          data-testid="theme-splash"
        >
          {/* Icy subtle striped backdrop (like the reference) */}
          <div className="absolute inset-0" style={{ background: "#F2F6FA", backgroundImage: "repeating-linear-gradient(45deg, rgba(120,140,165,0.055) 0 2px, transparent 2px 9px)" }} />
          <div className="relative flex flex-col items-center px-6">
            <motion.img
              src={LOGO_URL}
              alt="Care For Me"
              className="h-10 sm:h-14 w-auto mb-6 sm:mb-8"
              initial={{ y: -24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.15, type: "spring", stiffness: 140, damping: 14 }}
            />
            {/* The wreath with the days centred inside */}
            <motion.div
              className="relative grid place-items-center"
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 150, damping: 13 }}
            >
              <img src="/xmas-wreath.webp" alt="" className="h-48 w-48 sm:h-64 sm:w-64 object-contain" draggable={false} />
              <span className="absolute inset-0 grid place-items-center font-heading text-6xl sm:text-7xl font-bold text-brand-ink" data-testid="theme-splash-days">{days}</span>
            </motion.div>
            <motion.p
              className="mt-4 sm:mt-6 font-heading italic text-2xl sm:text-4xl font-bold text-brand-ink text-center"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5, type: "spring", stiffness: 130, damping: 15 }}
              data-testid="theme-splash-label"
            >
              {label}
            </motion.p>
            {/* Loading bar so visitors know the page is coming */}
            <motion.div
              className="mt-8 sm:mt-10 w-48 sm:w-64"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <div className="h-1.5 rounded-full bg-brand-ink/10 overflow-hidden" data-testid="theme-splash-loading">
                <motion.div className="h-full rounded-full bg-[#E8563F]" initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ delay: 0.75, duration: 3.1, ease: "linear" }} />
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

const ChristmasDecor = ({ splash }) => (
  <>
    <ParticleCanvas kind="snow" />
    <Garland />
    <SleighFlyby />
    <Snowflake className="bottom-24 left-6 h-10 w-10" />
    <Snowflake className="bottom-16 right-8 h-8 w-8 text-white/50" />
    <Snowflake className="top-40 right-16 h-7 w-7 text-white/60" />
    {splash && <ChristmasSplash />}
  </>
);

/* ---------- Halloween ---------- */
// Wings flap by folding toward the body (transform-origin at the body edge) — no squash/deform
const Bat = ({ delay, scale }) => (
  <div className="fx-bat" style={{ animationDelay: `${delay}s` }} aria-hidden="true">
    <svg width={46 * scale} height={22 * scale} viewBox="0 0 46 22" fill="#2A1F2E">
      <g className="fx-wing-l">
        <path d="M23 11 C18 4 10 1 2 4 C5 7 8 9 8 12 C9 16 14 18 19 16 C20 14 21 12 23 12 Z" />
      </g>
      <g className="fx-wing-r">
        <path d="M23 11 C28 4 36 1 44 4 C41 7 38 9 38 12 C37 16 32 18 27 16 C26 14 25 12 23 12 Z" />
      </g>
      <ellipse cx="23" cy="11.5" rx="4" ry="5.5" />
      <path d="M20.5 7 L22 2.5 L23.5 7 L25 2.5 L26 7 Z" />
      <circle cx="21.5" cy="10" r="0.9" fill="#E8563F" />
      <circle cx="24.5" cy="10" r="0.9" fill="#E8563F" />
    </svg>
  </div>
);

const Ghost = ({ style, delay }) => (
  <div className="fx-ghost absolute" style={{ ...style, animationDelay: `${delay}s` }} aria-hidden="true">
    <svg width="46" height="52" viewBox="0 0 46 52" fill="rgba(255,255,255,0.85)">
      <path d="M23 2C12 2 6 12 6 22v26l5-4 5 4 5-4 5 4 5-4 5 4 5-4V22C40 12 34 2 23 2z" />
      <circle cx="16" cy="20" r="3" fill="#2A1F2E" />
      <circle cx="30" cy="20" r="3" fill="#2A1F2E" />
      <ellipse cx="23" cy="30" rx="3.5" ry="4.5" fill="#2A1F2E" />
    </svg>
  </div>
);

const Pumpkin = ({ style }) => (
  <div className="fx-pumpkin absolute" style={style} aria-hidden="true">
    <svg width="72" height="60" viewBox="0 0 72 60">
      <ellipse cx="36" cy="34" rx="30" ry="24" fill="#E8651C" />
      <ellipse cx="36" cy="34" rx="16" ry="24" fill="#D9551E" />
      <path d="M34 10c0-4 4-6 6-4s0 6-2 8" fill="#4C6330" />
      <path d="M20 30l6 4-6 4zM52 30l-6 4 6 4z" fill="#2A1F2E" />
      <path d="M24 44q12 8 24 0-4 6-12 6t-12-6z" fill="#2A1F2E" />
    </svg>
  </div>
);

const Cobweb = ({ className }) => (
  <svg className={`fx-cobweb absolute h-28 w-28 text-brand-ink/15 ${className}`} viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
    <path d="M0 0 L100 100 M0 0 L60 100 M0 0 L100 60 M0 0 L25 100 M0 0 L100 25" />
    <path d="M14 14 Q20 20 14 28 M28 28 Q38 38 28 50 M42 42 Q56 56 42 72 M58 58 Q76 76 58 92" fill="none" />
    <path d="M14 14 Q22 16 28 12 M28 28 Q40 30 50 26 M42 42 Q58 44 72 40" fill="none" />
  </svg>
);

const HalloweenDecor = () => (
  <>
    <Cobweb className="top-16 left-0" />
    <Cobweb className="top-16 right-0 -scale-x-100" />
    <Bat delay={0} scale={1} />
    <Bat delay={5} scale={0.7} />
    <Bat delay={9.5} scale={1.25} />
    <Ghost style={{ bottom: "18%", left: "6%" }} delay={0} />
    <Ghost style={{ bottom: "26%", right: "7%" }} delay={2.4} />
    <Pumpkin style={{ bottom: "8px", left: "88px" }} />
    <Pumpkin style={{ bottom: "8px", right: "10px" }} />
  </>
);

/* ---------- Apokries (carnival) ---------- */
const Streamer = ({ className, colors }) => (
  <svg className={`fx-streamer absolute h-40 w-24 ${className}`} viewBox="0 0 60 160" fill="none" aria-hidden="true">
    <path d="M30 0 C 10 30, 50 50, 30 80 S 10 130, 30 160" stroke={colors[0]} strokeWidth="5" strokeLinecap="round" />
    <path d="M42 0 C 26 26, 56 52, 40 84 S 26 128, 42 156" stroke={colors[1]} strokeWidth="4" strokeLinecap="round" opacity="0.8" />
  </svg>
);

const ApokriesDecor = () => (
  <>
    <ParticleCanvas kind="confetti" />
    <Streamer className="top-14 left-3" colors={["#C42B9C", "#14A496"]} />
    <Streamer className="top-14 right-3 -scale-x-100" colors={["#F2C230", "#3D7BE0"]} />
    <svg className="absolute bottom-6 left-5 h-12 w-16" viewBox="0 0 64 40" aria-hidden="true">
      <path d="M4 12 Q20 2 32 10 Q44 2 60 12 Q58 30 44 26 Q36 34 32 26 Q28 34 20 26 Q6 30 4 12z" fill="#C42B9C" />
      <ellipse cx="19" cy="17" rx="6" ry="4" fill="#253" opacity="0.35" />
      <ellipse cx="45" cy="17" rx="6" ry="4" fill="#253" opacity="0.35" />
    </svg>
    <svg className="absolute bottom-10 right-6 h-10 w-14 rotate-12" viewBox="0 0 64 40" aria-hidden="true">
      <path d="M4 12 Q20 2 32 10 Q44 2 60 12 Q58 30 44 26 Q36 34 32 26 Q28 34 20 26 Q6 30 4 12z" fill="#14A496" />
    </svg>
  </>
);

/* ---------- Easter ---------- */
// Wings flap by folding around the body axis — the body itself never deforms
const Butterfly = ({ delay, color }) => (
  <div className="fx-butterfly" style={{ animationDelay: `${delay}s` }} aria-hidden="true">
    <svg width="36" height="28" viewBox="0 0 36 28">
      <g className="fx-wing-l">
        <path d="M18 14 C11 2 2 4 4 11 C1 17 9 25 18 16 Z" fill={color} />
      </g>
      <g className="fx-wing-r">
        <path d="M18 14 C25 2 34 4 32 11 C35 17 27 25 18 16 Z" fill={color} opacity="0.85" />
      </g>
      <rect x="16.6" y="7" width="2.8" height="14" rx="1.4" fill="#5C4A52" />
      <path d="M17 7 q-2 -4 -4 -5 M19 7 q2 -4 4 -5" stroke="#5C4A52" strokeWidth="1" fill="none" />
    </svg>
  </div>
);

const EasterEgg = ({ style, color, pattern }) => (
  <svg className="absolute h-12 w-9" style={style} viewBox="0 0 36 48" aria-hidden="true">
    <path d="M18 2C9 2 3 16 3 28a15 15 0 0 0 30 0C33 16 27 2 18 2z" fill={color} />
    {pattern === "dots" && <g fill="#fff" opacity="0.85"><circle cx="12" cy="22" r="2.4" /><circle cx="24" cy="30" r="2.4" /><circle cx="16" cy="36" r="2.4" /></g>}
    {pattern === "zig" && <path d="M5 26l5-5 5 5 5-5 5 5 5-5" stroke="#fff" strokeWidth="2.4" fill="none" opacity="0.85" />}
    {pattern === "stripe" && <g stroke="#fff" strokeWidth="3" opacity="0.85"><path d="M4 24h28" /><path d="M6 34h24" /></g>}
  </svg>
);

const EasterDecor = () => (
  <>
    <Butterfly delay={0} color="#B76BA3" />
    <Butterfly delay={7} color="#E8A2C0" />
    <Butterfly delay={12.5} color="#7CB350" />
    <div className="absolute bottom-2 left-4 flex items-end gap-2" aria-hidden="true">
      <EasterEgg style={{ position: "relative" }} color="#F2B8D0" pattern="dots" />
      <EasterEgg style={{ position: "relative", height: "2.4rem", width: "1.8rem" }} color="#A8D48A" pattern="zig" />
    </div>
    <div className="absolute bottom-2 right-4 flex items-end gap-2" aria-hidden="true">
      <EasterEgg style={{ position: "relative", height: "2.4rem", width: "1.8rem" }} color="#B7A2E0" pattern="stripe" />
      <EasterEgg style={{ position: "relative" }} color="#F5D78E" pattern="dots" />
    </div>
  </>
);

/* ---------- Summer ---------- */
const SummerDecor = () => (
  <>
    <svg className="fx-sun absolute top-16 right-6 h-16 w-16" viewBox="0 0 64 64" aria-hidden="true">
      <g className="fx-sun-rays" style={{ transformOrigin: "32px 32px" }}>
        {Array.from({ length: 12 }).map((_, i) => (
          <line key={i} x1="32" y1="4" x2="32" y2="12" stroke="#F2B230" strokeWidth="3.4" strokeLinecap="round" transform={`rotate(${i * 30} 32 32)`} />
        ))}
      </g>
      <circle cx="32" cy="32" r="13" fill="#F5C33B" />
      <circle cx="27" cy="29" r="2" fill="#B86E14" />
      <circle cx="37" cy="29" r="2" fill="#B86E14" />
      <path d="M26 36q6 5 12 0" stroke="#B86E14" strokeWidth="2.2" fill="none" strokeLinecap="round" />
    </svg>
    <div className="absolute bottom-0 left-0 right-0 h-20 overflow-hidden" aria-hidden="true">
      <svg className="fx-wave absolute bottom-0 h-16 w-[200%]" viewBox="0 0 1440 90" preserveAspectRatio="none">
        <path d="M0 50 Q 90 20 180 50 T 360 50 T 540 50 T 720 50 T 900 50 T 1080 50 T 1260 50 T 1440 50 V90 H0 Z" fill="rgba(14,143,168,0.16)" />
      </svg>
      <svg className="fx-wave-slow absolute bottom-0 h-12 w-[200%]" viewBox="0 0 1440 90" preserveAspectRatio="none">
        <path d="M0 55 Q 120 30 240 55 T 480 55 T 720 55 T 960 55 T 1200 55 T 1440 55 V90 H0 Z" fill="rgba(14,143,168,0.26)" />
      </svg>
    </div>
  </>
);

const DECOR = { halloween: HalloweenDecor, apokries: ApokriesDecor, easter: EasterDecor, summer: SummerDecor };

export const ThemeEffects = () => {
  const [cfg, setCfg] = useState({ active: "none", splash: false });
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    api.get("/theme").then((r) => setCfg({ active: r.data?.active || "none", splash: !!r.data?.splash })).catch(() => {});
  }, []);

  const theme = cfg.active;
  useEffect(() => {
    if (!isAdmin && theme && theme !== "none") document.documentElement.dataset.theme = theme;
    else delete document.documentElement.dataset.theme;
  }, [theme, isAdmin]);

  if (isAdmin || !theme || theme === "none") return null;
  const Decor = theme === "christmas" ? null : DECOR[theme];
  return (
    <div className="theme-fx" data-testid={`theme-fx-${theme}`} aria-hidden="true">
      {theme === "christmas" ? <ChristmasDecor splash={cfg.splash} /> : Decor ? <Decor /> : null}
    </div>
  );
};

export default ThemeEffects;
